import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const TITLE_MAX = 60;
const DESC_MAX = 160;

const EMPTY = {
    title: "", meta_description: "", keywords: "",
    og_title: "", og_description: "", og_image: "", twitter_card: "summary_large_image",
    business_name: "", business_phone: "", business_address: "", business_region: "", google_review_link: "",
};

/**
 * AdminSeo — "SEO & Meta". 3 seções (Core Meta, Social/OG, Local Business)
 * + preview de compartilhamento (WhatsApp/social) + persistência em
 * /admin/seo (documento único, consumido publicamente via /public/seo).
 */
const AdminSeo = () => {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const load = async () => {
        setLoading(true);
        try {
            const doc = await api.get("/admin/seo");
            setForm({ ...EMPTY, ...doc });
        } catch (e) {
            toast.error("Falha ao carregar SEO", { description: e?.message || "Tente novamente." });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const doc = await api.patch("/admin/seo", form);
            setForm({ ...EMPTY, ...doc });
            toast.success("SEO atualizado", { description: "As páginas públicas já refletem as novas tags." });
        } catch (e) {
            toast.error("Falha ao salvar", { description: e?.message || "Tente novamente." });
        } finally {
            setSaving(false);
        }
    };

    const previewTitle = form.og_title || form.title || "Título da página";
    const previewDesc = form.og_description || form.meta_description || "Descrição da página";
    const previewDomain = (typeof window !== "undefined" ? window.location.hostname : "kinng.app");

    return (
        <div className="space-y-6" data-testid="admin-seo-view">
            <header>
                <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma</div>
                <h1 className="salon-heading text-3xl text-white mt-1">SEO &amp; Meta</h1>
                <p className="text-sm text-slate-400 mt-1">
                    Título, descrição, Open Graph e dados de negócio local usados nas páginas públicas.
                </p>
            </header>

            {loading ? (
                <div className="py-16 text-center text-slate-500 text-sm">Carregando…</div>
            ) : (
                <div className="grid lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 space-y-5">
                        {/* Core Meta Tags */}
                        <Section icon="fa-heading" title="Core Meta Tags" testid="admin-seo-section-core">
                            <FieldWithCounter
                                label="Título da página" max={TITLE_MAX}
                                value={form.title} onChange={(v) => set({ title: v })}
                                testId="admin-seo-title" placeholder="Kinng.app — Gestão para salões e barbearias"
                            />
                            <FieldWithCounter
                                label="Meta descrição" max={DESC_MAX} textarea
                                value={form.meta_description} onChange={(v) => set({ meta_description: v })}
                                testId="admin-seo-description" placeholder="Agenda, PDV, comissões e WhatsApp automático em uma plataforma."
                            />
                            <Field
                                label="Palavras-chave (separadas por vírgula)"
                                value={form.keywords} onChange={(v) => set({ keywords: v })}
                                testId="admin-seo-keywords" placeholder="gestão de salão, sistema para barbearia, agenda online"
                            />
                        </Section>

                        {/* Social / Open Graph */}
                        <Section icon="fa-share-nodes" title="Social / Open Graph" testid="admin-seo-section-og">
                            <Field label="OG Título" value={form.og_title} onChange={(v) => set({ og_title: v })}
                                testId="admin-seo-og-title" placeholder="Usa o Título da página se vazio" />
                            <Field label="OG Descrição" textarea value={form.og_description} onChange={(v) => set({ og_description: v })}
                                testId="admin-seo-og-description" placeholder="Usa a Meta descrição se vazio" />
                            <Field label="OG Imagem (URL)" value={form.og_image} onChange={(v) => set({ og_image: v })}
                                testId="admin-seo-og-image" placeholder="https://kinng.app/og-image.png" mono />
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Twitter card</label>
                                <select
                                    data-testid="admin-seo-twitter-card"
                                    value={form.twitter_card}
                                    onChange={(e) => set({ twitter_card: e.target.value })}
                                    className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60"
                                >
                                    <option value="summary">summary</option>
                                    <option value="summary_large_image">summary_large_image</option>
                                </select>
                            </div>
                        </Section>

                        {/* Local Business */}
                        <Section icon="fa-shop" title="Local Business" testid="admin-seo-section-business">
                            <Field label="Nome do negócio" value={form.business_name} onChange={(v) => set({ business_name: v })}
                                testId="admin-seo-business-name" placeholder="Kinng.app" />
                            <Field label="Telefone" value={form.business_phone} onChange={(v) => set({ business_phone: v })}
                                testId="admin-seo-business-phone" placeholder="+55 11 90000-0000" mono />
                            <Field label="Endereço" value={form.business_address} onChange={(v) => set({ business_address: v })}
                                testId="admin-seo-business-address" placeholder="Rua Exemplo, 123 — São Paulo, SP" />
                            <Field label="Região" value={form.business_region} onChange={(v) => set({ business_region: v })}
                                testId="admin-seo-business-region" placeholder="BR" mono />
                            <Field label="Link de avaliação Google" value={form.google_review_link} onChange={(v) => set({ google_review_link: v })}
                                testId="admin-seo-review-link" placeholder="https://g.page/r/..." mono />
                        </Section>

                        <div className="flex justify-end">
                            <button
                                data-testid="admin-seo-save"
                                onClick={handleSave}
                                disabled={saving}
                                className="min-h-[44px] px-6 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold text-xs uppercase tracking-wider transition-all disabled:opacity-40"
                            >
                                {saving ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Salvando…</> : "Salvar SEO"}
                            </button>
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-6 space-y-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                Preview · WhatsApp / Social
                            </div>
                            <div
                                data-testid="admin-seo-preview-card"
                                className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden max-w-sm"
                            >
                                <div className="h-40 bg-slate-800 flex items-center justify-center overflow-hidden">
                                    {form.og_image ? (
                                        <img src={form.og_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                    ) : (
                                        <i className="fa-solid fa-image text-slate-600 text-3xl"></i>
                                    )}
                                </div>
                                <div className="p-3 bg-slate-950">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{previewDomain}</div>
                                    <div className="text-sm text-white font-semibold truncate mt-0.5">{previewTitle}</div>
                                    <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{previewDesc}</div>
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                Aproximação de como o link aparece ao ser colado no WhatsApp ou redes sociais — o
                                resultado real pode variar por plataforma e cache do link.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Section = ({ icon, title, testid, children }) => (
    <section data-testid={testid} className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
            <i className={`fa-solid ${icon} text-indigo-400 text-sm`}></i>
            <h2 className="text-sm font-semibold text-white">{title}</h2>
        </div>
        {children}
    </section>
);

const Field = ({ label, value, onChange, testId, placeholder, mono, textarea }) => {
    const Tag = textarea ? "textarea" : "input";
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
            <Tag
                data-testid={testId}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={textarea ? 3 : undefined}
                className={`mt-1.5 w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/60 transition-colors ${mono ? "font-mono" : ""} ${textarea ? "resize-y" : "min-h-[44px]"}`}
            />
        </label>
    );
};

const FieldWithCounter = ({ label, value, onChange, max, testId, placeholder, textarea }) => {
    const len = (value || "").length;
    const over = len > max;
    const Tag = textarea ? "textarea" : "input";
    return (
        <label className="block">
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
                <span className={`text-[10px] font-mono ${over ? "text-red-400" : "text-slate-500"}`}>{len}/{max}</span>
            </div>
            <Tag
                data-testid={testId}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={textarea ? 3 : undefined}
                className={`mt-1.5 w-full rounded-xl border ${over ? "border-red-500/50" : "border-white/10"} bg-slate-950/60 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/60 transition-colors ${textarea ? "resize-y" : "min-h-[44px]"}`}
            />
        </label>
    );
};

export default AdminSeo;
